import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Gift, Info, Sparkles } from "lucide-react";
import { api, formatApiError } from "../api";
import { REVENUE_EXTRAS } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusPill } from "../components/common/StatusPill";
import { FormField } from "../components/common/FormField";
import { SubmitButton } from "../components/common/SubmitButton";
import { toast } from "../components/ui/sonner";

const TABS = [{ key: "loyalty", label: "Loyalty & Referrals" }, { key: "ads", label: "Ad-revenue share" }];

// UX-GAP-01 §1.9: Owner Cockpit "Revenue Extras" — was a StubPage. Loyalty & Referrals tab is
// real (`/loyalty/{id}`, `/loyalty/{id}/credit`, `/referrals`). Ad-revenue tab is an honest
// "not connected" state — see this file's testIds comment for why.
export default function RevenueExtras() {
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("loyalty");

  return (
    <div className="space-y-5 max-w-3xl" data-testid={REVENUE_EXTRAS.page}>
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Revenue Extras</h1>
        <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Everything beyond membership dues — loyalty, referrals, and ad revenue.</p>
      </motion.div>

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={REVENUE_EXTRAS.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "loyalty" && <LoyaltyReferralsTab />}
      {tab === "ads" && <AdRevenueTab />}
    </div>
  );
}

function useMemberSearch(query) {
  return useQuery({
    queryKey: ["revenue-extras-member-search", query],
    queryFn: async () => {
      const isPhone = /^\+?\d[\d\s-]{5,}$/.test(query);
      const params = isPhone ? { phone: query.replace(/[\s-]/g, "") } : { name: query };
      return (await api.get("/members", { params: { ...params, limit: 10 } })).data.members;
    },
    enabled: !!query,
  });
}

function LoyaltyReferralsTab() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [member, setMember] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const searchQ = useMemberSearch(submitted);

  const accountQ = useQuery({
    queryKey: ["revenue-extras-loyalty", member?.id],
    queryFn: async () => (await api.get(`/loyalty/${member.id}`)).data,
    enabled: !!member,
  });

  const creditMutation = useMutation({
    mutationFn: () => api.post(`/loyalty/${member.id}/credit`, { amount: Math.round(Number(amount) * 100), reason }),
    onSuccess: () => { toast.success("Loyalty credit awarded."); setAmount(""); setReason(""); accountQ.refetch(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div className="space-y-5">
      <div className="solid-surface rounded-[14px] p-5 space-y-3">
        <p className="font-semibold flex items-center gap-2" style={{ color: "var(--scarlet-text)" }}><Gift size={16} style={{ color: "var(--scarlet-accent)" }} /> Award a loyalty credit</p>
        <form onSubmit={(e) => { e.preventDefault(); setMember(null); setSubmitted(query.trim()); }} className="flex gap-2">
          <Input data-testid={REVENUE_EXTRAS.memberSearchInput} placeholder="Member name or phone…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <Button type="submit" data-testid={REVENUE_EXTRAS.memberSearchButton} disabled={!query.trim()} className="text-white" style={{ background: "var(--scarlet-accent)" }}>Find</Button>
        </form>
        {!member && searchQ.data?.map((m) => (
          <button key={m.id} data-testid={REVENUE_EXTRAS.memberRow(m.id)} onClick={() => setMember(m)} className="w-full flex items-center justify-between rounded-[10px] px-3.5 py-2.5 text-left" style={{ border: "1px solid var(--scarlet-hairline)" }}>
            <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{m.profile.name} <span className="ml-1 font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{m.profile.phone}</span></p>
          </button>
        ))}
        {member && accountQ.data && (
          <div className="space-y-3 pt-2" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{member.profile.name}</p>
              <div className="flex items-center gap-2">
                <span data-testid={REVENUE_EXTRAS.loyaltyPoints} className="text-sm font-bold" style={{ color: "var(--scarlet-accent)" }}>{accountQ.data.points} pts</span>
                <StatusPill status="active" label={accountQ.data.tier} />
                <span className="sr-only" data-testid={REVENUE_EXTRAS.loyaltyTier}>{accountQ.data.tier}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Credit amount (₹)" type="number" testId={REVENUE_EXTRAS.creditAmountInput} value={amount} onChange={setAmount} />
              <FormField label="Reason" testId={REVENUE_EXTRAS.creditReasonInput} value={reason} onChange={setReason} />
            </div>
            <SubmitButton testId={REVENUE_EXTRAS.creditSubmit} disabled={!amount || !reason.trim()} pending={creditMutation.isPending} onClick={() => creditMutation.mutate()} label="Award credit" />
          </div>
        )}
      </div>

      <ReferralForm />
    </div>
  );
}

function ReferralForm() {
  const { branches, selectedBranchId } = useAppData();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [referrer, setReferrer] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [branchId, setBranchId] = useState(selectedBranchId !== "all" ? selectedBranchId : "");
  const searchQ = useMemberSearch(submitted);

  const mutation = useMutation({
    mutationFn: () => api.post("/referrals", { referrer_member_ref: referrer.id, referred: { name, phone }, branch_id: branchId }),
    onSuccess: (res) => {
      toast.success(`Referral created — ${res.data.points_awarded} points awarded to ${referrer.profile.name}.`);
      setReferrer(null); setName(""); setPhone(""); setQuery(""); setSubmitted("");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3">
      <p className="font-semibold flex items-center gap-2" style={{ color: "var(--scarlet-text)" }}><Sparkles size={16} style={{ color: "var(--scarlet-accent)" }} /> Log a referral</p>
      {!referrer && (
        <>
          <form onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim()); }} className="flex gap-2">
            <Input data-testid={REVENUE_EXTRAS.referrerSearchInput} placeholder="Referring member's name or phone…" value={query} onChange={(e) => setQuery(e.target.value)} />
            <Button type="submit" variant="outline" disabled={!query.trim()}>Find</Button>
          </form>
          {searchQ.data?.map((m) => (
            <button key={m.id} data-testid={REVENUE_EXTRAS.referrerRow(m.id)} onClick={() => setReferrer(m)} className="w-full flex items-center justify-between rounded-[10px] px-3.5 py-2.5 text-left" style={{ border: "1px solid var(--scarlet-hairline)" }}>
              <p className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{m.profile.name} <span className="ml-1 font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{m.profile.phone}</span></p>
            </button>
          ))}
        </>
      )}
      {referrer && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Referred by <strong style={{ color: "var(--scarlet-text)" }}>{referrer.profile.name}</strong> · <button onClick={() => setReferrer(null)} className="underline">change</button></p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Referred person's name" required testId={REVENUE_EXTRAS.referredNameInput} value={name} onChange={setName} />
            <FormField label="Referred person's phone" required type="tel" testId={REVENUE_EXTRAS.referredPhoneInput} value={phone} onChange={setPhone} />
          </div>
          <div className="space-y-1.5">
            <Label>Branch</Label>
            <Select onValueChange={setBranchId} value={branchId || undefined}>
              <SelectTrigger data-testid={REVENUE_EXTRAS.referralBranchSelect}><SelectValue placeholder="Which branch?" /></SelectTrigger>
              <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <SubmitButton testId={REVENUE_EXTRAS.referralSubmit} disabled={!name.trim() || !phone.trim() || !branchId} pending={mutation.isPending} onClick={() => mutation.mutate()} label="Log referral" />
        </div>
      )}
    </div>
  );
}

// Ad-revenue settlement/proof-of-play endpoints in `routers/broadcast.py` are ALL
// `require_kairoo_permission("broadcast")` — Kairoo-only. No data-plane endpoint exists for a
// gym's own Super Admin to read their own ad earnings, so this is an honest gap, not a chart with
// invented numbers.
function AdRevenueTab() {
  return (
    <div className="solid-surface rounded-[14px] p-8 flex flex-col items-center text-center gap-3" data-testid={REVENUE_EXTRAS.adRevenueNotConnected}>
      <Info size={22} style={{ color: "var(--scarlet-text-secondary)" }} />
      <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>Ad-revenue reporting isn't connected yet</p>
      <p className="text-sm max-w-md" style={{ color: "var(--scarlet-text-secondary)" }}>
        Your BroadcastOS screens are active, but settlement reporting for your organization needs to be switched on from the Kairoo side. Reach out to your Kairoo account manager to get this connected.
      </p>
    </div>
  );
}
